/**
 * Admin erasure requests — the client half of the admin request-handling tool
 * (Phase 118-09). Backs `src/app/(admin)/erasure-requests.tsx`.
 *
 * Server: the `admin_*` actions of `supabase/functions/account-deletion`
 * (admin.ts). Admin authority (admin_roles, tier super_admin or support) AND an
 * MFA-verified (AAL2) session are required for every one of them; an admin who
 * has never enrolled MFA gets 403 `mfa_required`, not a silent pass. The tool
 * refuses the caller's own account and any other administrator's.
 *
 *   admin_lookup   email → the account id + created date + any open request
 *   admin_request  file (30 days) or erase immediately; an existing pending
 *                  request is EXPEDITED instead of duplicated
 *   admin_status   the request + its erasure_events outcome — the proof to send
 *                  back to the person
 *   admin_list     pending / processing / partial / failed requests
 *   admin_requeue  a failed request gets five more attempts (holds kept)
 *
 * 119-07, closed companies (119-06 actions):
 *   admin_company_lookup       company id → its lifecycle, members, any data deletion
 *   admin_company_data_delete  file the deletion of a SEALED company's data (no
 *                              grace; journey snapshot first; score history kept)
 *   admin_company_reopen       hand a sealed company back to a returning founder —
 *                              the proof is THEIR verified claim, resolved in SQL;
 *                              this client never sends one
 *   admin_company_requeue      a failed company-data deletion gets more attempts
 *
 * This module branches on `code` and status, never on message text, and never
 * holds an address or a name: the lookup's answer is an id.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { isBackendEnabled, isMockMode } from '@/lib/config/app-mode';
import { reportError } from '@/lib/errors';

// -----------------------------------------------------------------------------
// Shapes
// -----------------------------------------------------------------------------

export type ErasureRequestStatus = 'pending' | 'cancelled' | 'processing' | 'completed' | 'partial' | 'failed';

/** Mirrors ADMIN_REASONS in supabase/functions/account-deletion/admin.ts. */
export const ERASURE_REASONS = [
  { code: 'subject_request', label: 'The person asked (verified, by email)' },
  { code: 'test_account', label: 'Internal test account' },
] as const;
export type ErasureReason = (typeof ERASURE_REASONS)[number]['code'];

export interface ErasureLookup {
  account: { userId: string; createdAt: string } | null;
  openRequest: { id: string; status: ErasureRequestStatus; scheduledFor: string; source: 'self' | 'admin' } | null;
}

export interface ErasureRequestSummary {
  id: string;
  status: ErasureRequestStatus;
  source: 'self' | 'admin';
  requestedAt: string;
  scheduledFor: string;
  completedAt: string | null;
  attempts: number;
  lastError: string | null;
  reason: string | null;
  holds: number;
}

export interface ErasureProof {
  id: string;
  outcome: 'completed' | 'partial' | 'failed';
  startedAt: string;
  finishedAt: string | null;
  counts: Record<string, unknown>;
  failed: Record<string, unknown>;
  ownership: Record<string, unknown>;
}

export interface ErasureFiled {
  requestId: string;
  scheduledFor: string;
  /** An existing pending request was moved to now() instead of a new one filed. */
  expedited: boolean;
  /** 207: recorded, but a grace step (key revocation, billing pause, email) failed. */
  failedSteps: string[];
}

// -----------------------------------------------------------------------------
// Errors
// -----------------------------------------------------------------------------

export type ErasureAdminErrorKind =
  | 'mfa_required'
  | 'not_admin'
  | 'target_is_admin'
  | 'confirmation_mismatch'
  | 'already_scheduled'
  | 'erasure_in_progress'
  | 'account_not_found'
  | 'demo_account'
  | 'self_request'
  | 'not_failed'
  | 'audit_unavailable'
  | 'invalid_input'
  | 'unavailable_offline'
  // 119-07: closed-company actions
  | 'company_not_found'
  | 'company_not_closed'
  | 'company_data_deleted'
  | 'company_has_members'
  | 'company_not_eligible'
  | 'data_deletion_outstanding'
  | 'proof_required'
  | 'proof_stale'
  | 'erasure_hold'
  | 'self_grant'
  | 'failed';

export class ErasureAdminError extends Error {
  readonly kind: ErasureAdminErrorKind;
  readonly userMessage: string;
  readonly status?: number;
  /** The server's refusal code, when there was one (e.g. `aal2_required`). */
  readonly code: string | null;

  constructor(kind: ErasureAdminErrorKind, userMessage: string, detail?: string, status?: number, code?: string | null) {
    super(`erasure_admin_${kind}${detail ? `: ${detail}` : ''}`);
    this.name = 'ErasureAdminError';
    this.kind = kind;
    this.userMessage = userMessage;
    this.status = status;
    this.code = code ?? null;
  }
}

const GENERIC = 'The request could not be completed. Please try again.';

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : {};
}

/** Map a non-2xx status + body to the typed error. Pure; exported for tests. */
export function mapErasureRefusal(status: number, body: Record<string, unknown>): ErasureAdminError {
  const code = typeof body.code === 'string' ? body.code : null;
  const e = (kind: ErasureAdminErrorKind, msg: string) => new ErasureAdminError(kind, msg, code ?? `http_${status}`, status, code);
  switch (code) {
    case 'aal2_required':
    case 'mfa_required':
      return e('mfa_required', 'Erasure tools need an MFA-verified session. Set up an authenticator app in Settings, then sign in again.');
    case 'admin_required':
      return e('not_admin', 'Your account does not hold administrative authority.');
    case 'admin_role_insufficient':
      return e('not_admin', 'Handling erasure requests needs the super_admin or support admin role.');
    case 'target_is_admin':
      return e('target_is_admin', 'This account holds administrative authority. Remove it first (an operator step), then erase.');
    case 'confirmation_mismatch':
      return e('confirmation_mismatch', 'The pasted id does not match the account found. Copy the id shown above.');
    case 'already_scheduled':
      return e('already_scheduled', 'This account is already scheduled for deletion. Use “Erase immediately” to expedite it.');
    case 'erasure_in_progress':
      return e('erasure_in_progress', 'This account’s erasure is already running and can no longer be changed.');
    case 'account_not_found':
      return e('account_not_found', 'No account has this id any more.');
    case 'demo_account':
      return e('demo_account', 'This is a shared demo account and cannot be deleted.');
    case 'self_request_not_allowed':
      return e('self_request', 'Delete your own account from Settings, not from the admin tool.');
    case 'not_failed':
      return e('not_failed', 'Only a failed request can be re-queued. Refresh the list.');
    case 'audit_unavailable':
      return e('audit_unavailable', 'The admin audit log is unavailable, so nothing was done. Try again shortly.');
    case 'invalid_email':
      return e('invalid_input', 'Enter the email address the person wrote from.');
    // 119-07: closed companies
    case 'company_not_found':
      return e('company_not_found', 'No company has this id.');
    case 'company_not_closed':
      return e('company_not_closed', 'This company is operating. Only a closed company’s data is deleted or reopened here.');
    case 'company_data_deleted':
      return e('company_data_deleted', 'This company’s data is already deleted. Its tombstone keeps the status and score history and cannot be reopened.');
    case 'company_has_members':
      return e('company_has_members', 'This company has an active member, so nothing is done from here.');
    case 'not_eligible':
      return e('company_not_eligible', 'The company changed since the lookup (it is no longer sealed with its data and nobody in it). Look it up again.');
    case 'data_deletion_outstanding':
      return e('data_deletion_outstanding', 'A deletion of this company’s data is outstanding, so it cannot be reopened.');
    case 'proof_required':
      return e('proof_required', 'The claimant holds no verified claim on this company. They must verify it first.');
    case 'PROOF_STALE':
      return e('proof_stale', 'The claimant’s verification predates the close. They must verify the company again.');
    case 'erasure_hold':
      return e('erasure_hold', 'The erasure of this company’s last member is still running. Try again once it completes.');
    case 'not_reopenable':
      return e('company_not_eligible', 'The company can no longer be reopened (it changed since the lookup). Look it up again.');
    case 'self_grant_not_allowed':
      return e('self_grant', 'An admin cannot reopen a company for themselves.');
    case 'reason_required':
    case 'invalid_user_id':
    case 'invalid_request_id':
    case 'invalid_startup_id':
    case 'proof_not_accepted':
      return e('invalid_input', GENERIC);
    default:
      return e('failed', GENERIC);
  }
}

// -----------------------------------------------------------------------------
// Transport
// -----------------------------------------------------------------------------

type AdminAction =
  | 'admin_lookup'
  | 'admin_request'
  | 'admin_status'
  | 'admin_list'
  | 'admin_requeue'
  | 'admin_company_lookup'
  | 'admin_company_data_delete'
  | 'admin_company_reopen'
  | 'admin_company_requeue';

async function call(action: AdminAction, extra: Record<string, unknown> = {}): Promise<{ status: number; body: Record<string, unknown> }> {
  if (!isBackendEnabled() || isMockMode()) {
    throw new ErasureAdminError('unavailable_offline', 'Erasure requests need a live backend. They are not available in offline sample mode.');
  }
  const { data, error, response } = await supabase.functions.invoke('account-deletion', {
    body: { action, ...extra },
  });
  if (!error) return { status: response?.status ?? 200, body: asRecord(data) };

  const err = error as { name?: unknown; message?: unknown; context?: { status?: unknown; json?: () => Promise<unknown> } };
  if (err.name !== 'FunctionsHttpError' || typeof err.context?.status !== 'number') {
    throw new ErasureAdminError('failed', GENERIC, typeof err.message === 'string' ? err.message : String(error));
  }
  let body: Record<string, unknown> = {};
  try {
    body = asRecord(await err.context.json?.());
  } catch (parseErr) {
    reportError(parseErr, { context: `erasureAdmin.${action}.errorBody`, level: 'warning' });
  }
  throw mapErasureRefusal(err.context.status, body);
}

function invalid(detail: string): ErasureAdminError {
  return new ErasureAdminError('failed', GENERIC, `invalid_response: ${detail}`);
}

const STATUSES: readonly string[] = ['pending', 'cancelled', 'processing', 'completed', 'partial', 'failed'];

function str(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}

// -----------------------------------------------------------------------------
// Parsers (pure; exported for tests)
// -----------------------------------------------------------------------------

export function parseSummary(v: unknown): ErasureRequestSummary {
  const r = asRecord(v);
  if (typeof r.id !== 'string' || typeof r.status !== 'string' || !STATUSES.includes(r.status)) throw invalid('summary');
  return {
    id: r.id,
    status: r.status as ErasureRequestStatus,
    source: r.source === 'admin' ? 'admin' : 'self',
    requestedAt: str(r.requested_at) ?? '',
    scheduledFor: str(r.scheduled_for) ?? '',
    completedAt: str(r.completed_at),
    attempts: typeof r.attempts === 'number' ? r.attempts : 0,
    lastError: str(r.last_error),
    reason: str(r.reason),
    holds: typeof r.holds === 'number' ? r.holds : 0,
  };
}

export function parseLookup(body: Record<string, unknown>): ErasureLookup {
  if (!('account' in body)) throw invalid('lookup');
  const a = body.account === null ? null : asRecord(body.account);
  const o = body.open_request ? asRecord(body.open_request) : null;
  return {
    account: a && typeof a.user_id === 'string' ? { userId: a.user_id, createdAt: str(a.created_at) ?? '' } : null,
    openRequest: o && typeof o.id === 'string' && typeof o.status === 'string'
      ? {
          id: o.id,
          status: o.status as ErasureRequestStatus,
          scheduledFor: str(o.scheduled_for) ?? '',
          source: o.source === 'admin' ? 'admin' : 'self',
        }
      : null,
  };
}

export function parseProof(body: Record<string, unknown>): { request: ErasureRequestSummary; proof: ErasureProof | null } {
  const request = parseSummary(body.request);
  const p = body.proof ? asRecord(body.proof) : null;
  const proof: ErasureProof | null = p && typeof p.id === 'string'
    ? {
        id: p.id,
        outcome: p.outcome === 'completed' || p.outcome === 'failed' ? p.outcome : 'partial',
        startedAt: str(p.started_at) ?? '',
        finishedAt: str(p.finished_at),
        counts: asRecord(p.counts),
        failed: asRecord(p.failed),
        ownership: asRecord(p.ownership),
      }
    : null;
  return { request, proof };
}

// -----------------------------------------------------------------------------
// Calls
// -----------------------------------------------------------------------------

export async function lookupAccount(email: string): Promise<ErasureLookup> {
  return parseLookup((await call('admin_lookup', { email })).body);
}

export async function fileErasure(input: {
  userId: string;
  confirmUserId: string;
  reason: ErasureReason;
  immediate: boolean;
}): Promise<ErasureFiled> {
  const { body } = await call('admin_request', {
    user_id: input.userId,
    confirm_user_id: input.confirmUserId,
    reason: input.reason,
    immediate: input.immediate,
  });
  const req = asRecord(body.request);
  if (typeof req.id !== 'string' || typeof req.scheduled_for !== 'string') throw invalid('request');
  return {
    requestId: req.id,
    scheduledFor: req.scheduled_for,
    expedited: body.expedited === true,
    failedSteps: Array.isArray(body.failed) ? body.failed.filter((f): f is string => typeof f === 'string') : [],
  };
}

export async function listErasureRequests(): Promise<ErasureRequestSummary[]> {
  const { body } = await call('admin_list');
  if (!Array.isArray(body.requests)) throw invalid('list');
  return body.requests.map(parseSummary);
}

export async function getErasureProof(requestId: string) {
  return parseProof((await call('admin_status', { request_id: requestId })).body);
}

export async function requeueErasure(requestId: string): Promise<void> {
  await call('admin_requeue', { request_id: requestId });
}

// -----------------------------------------------------------------------------
// 119-07: closed companies
// -----------------------------------------------------------------------------

/** Mirrors COMPANY_DATA_REASONS in supabase/functions/account-deletion/admin.ts. */
export const COMPANY_DATA_REASONS = [
  { code: 'verified_founder_request', label: 'A verified former founder asked (privacy@)' },
  { code: 'admin_reviewed_request', label: 'Someone else asked (e.g. the acquirer); I reviewed it' },
] as const;
export type CompanyDataReason = (typeof COMPANY_DATA_REASONS)[number]['code'];

/** The only reopen reason the server accepts. */
export const COMPANY_REOPEN_REASON = 'verified_founder_return' as const;

export type CompanyLifecycleStatus = 'closed' | 'acquired' | 'bankrupt';

export interface CompanyAdminState {
  startupId: string;
  name: string | null;
  lifecycle: { status: CompanyLifecycleStatus; dataState: 'sealed' | 'deleted'; declaredVia: string | null } | null;
  activeMembers: number;
  dataDeletion: { id: string; status: string; attempts: number; lastError: string | null } | null;
}

const LIFECYCLE: readonly string[] = ['closed', 'acquired', 'bankrupt'];

/** The lookup's `company` (null = no company has this id). Pure; exported for tests. */
export function parseCompanyState(body: Record<string, unknown>): CompanyAdminState | null {
  if (!('company' in body)) throw invalid('company lookup');
  if (body.company === null) return null;
  const c = asRecord(body.company);
  if (typeof c.startup_id !== 'string') throw invalid('company lookup: id');
  const l = c.lifecycle ? asRecord(c.lifecycle) : null;
  const d = c.data_deletion ? asRecord(c.data_deletion) : null;
  return {
    startupId: c.startup_id,
    name: str(c.name),
    lifecycle: l && typeof l.status === 'string' && LIFECYCLE.includes(l.status)
      ? {
          status: l.status as CompanyLifecycleStatus,
          dataState: l.data_state === 'deleted' ? 'deleted' : 'sealed',
          declaredVia: str(l.declared_via),
        }
      : null,
    activeMembers: typeof c.active_members === 'number' ? c.active_members : 0,
    dataDeletion: d && typeof d.id === 'string'
      ? {
          id: d.id,
          status: str(d.status) ?? 'pending',
          attempts: typeof d.attempts === 'number' ? d.attempts : 0,
          lastError: str(d.last_error),
        }
      : null,
  };
}

/** What the screen may offer for a looked-up company (the server re-checks all of it). Pure. */
export function companyActions(c: CompanyAdminState): { canDeleteData: boolean; canReopen: boolean; canRequeue: boolean } {
  const sealedWithData = !!c.lifecycle && c.lifecycle.dataState === 'sealed' && c.activeMembers === 0;
  const outstanding = !!c.dataDeletion && c.dataDeletion.status !== 'completed';
  return {
    canDeleteData: sealedWithData && !outstanding,
    canReopen: sealedWithData && !outstanding,
    canRequeue: c.dataDeletion?.status === 'failed',
  };
}

export async function lookupCompany(startupId: string): Promise<CompanyAdminState | null> {
  return parseCompanyState((await call('admin_company_lookup', { startup_id: startupId.trim() })).body);
}

export async function requestCompanyDataDeletion(input: {
  startupId: string;
  confirmStartupId: string;
  reason: CompanyDataReason;
}): Promise<{ requestId: string; status: string; created: boolean }> {
  const { body } = await call('admin_company_data_delete', {
    startup_id: input.startupId,
    confirm_startup_id: input.confirmStartupId.trim(),
    reason: input.reason,
  });
  const r = asRecord(body.request);
  if (typeof r.id !== 'string') throw invalid('company data delete');
  return { requestId: r.id, status: str(r.status) ?? 'pending', created: body.created === true };
}

/**
 * Reopen for a returning founder. Deliberately sends NO proof: the server
 * refuses any proof field (400 proof_not_accepted) and resolves the claimant's
 * own fresh verified claim itself (decision #11).
 */
export async function reopenCompany(input: {
  startupId: string;
  confirmStartupId: string;
  claimantUserId: string;
  confirmClaimantUserId: string;
}): Promise<void> {
  await call('admin_company_reopen', {
    startup_id: input.startupId,
    confirm_startup_id: input.confirmStartupId.trim(),
    claimant_user_id: input.claimantUserId.trim(),
    confirm_claimant_user_id: input.confirmClaimantUserId.trim(),
    reason: COMPANY_REOPEN_REASON,
  });
}

export async function requeueCompanyDataDeletion(requestId: string): Promise<void> {
  await call('admin_company_requeue', { request_id: requestId });
}

// -----------------------------------------------------------------------------
// Hooks
// -----------------------------------------------------------------------------

export const ERASURE_REQUESTS_KEY = ['admin', 'erasure-requests'] as const;
export const erasureProofKey = (requestId: string) => ['admin', 'erasure-proof', requestId] as const;

export function useErasureRequests() {
  return useQuery({
    queryKey: ERASURE_REQUESTS_KEY,
    queryFn: listErasureRequests,
    // Every list read is an audited admin action; do not re-read on focus.
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useErasureProof(requestId: string | null) {
  return useQuery({
    queryKey: erasureProofKey(requestId ?? ''),
    queryFn: () => getErasureProof(requestId!),
    enabled: !!requestId,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useLookupAccount() {
  return useMutation({ mutationFn: lookupAccount });
}

export function useFileErasure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fileErasure,
    onSuccess: () => qc.invalidateQueries({ queryKey: ERASURE_REQUESTS_KEY }),
  });
}

export function useRequeueErasure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: requeueErasure,
    onSuccess: (_d, requestId) => {
      qc.invalidateQueries({ queryKey: ERASURE_REQUESTS_KEY });
      qc.invalidateQueries({ queryKey: erasureProofKey(requestId) });
    },
  });
}

export function useLookupCompany() {
  return useMutation({ mutationFn: lookupCompany });
}

export function useRequestCompanyDataDeletion() {
  return useMutation({ mutationFn: requestCompanyDataDeletion });
}

export function useReopenCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: reopenCompany,
    // A reopened company is no longer sealed: drop every cached card.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['company-public-cards'] }),
  });
}

export function useRequeueCompanyDataDeletion() {
  return useMutation({ mutationFn: requeueCompanyDataDeletion });
}
